import { useContext, useState } from 'react';

import { ModalType } from '@/API/models';
import {
  DEFAULT_PLAN_ID,
  SUBSCRIPTION_PLANS_CONFIG,
  SubscriptionPlan,
} from '@/components/SubscriptionCard/SubscriptionCard.utils';
import { EditProfileContext } from '@/contexts/EditProfileProvider';
import { useCurrentUser } from '@/Utils/stripeHooks/useCurrentUser';
import { usePaymentMethods } from '@/Utils/stripeHooks/usePaymentMethods';
import { useSubscriptionCheckout } from '@/Utils/stripeHooks/useSubscriptionCheckout';
import { useSubscriptions } from '@/Utils/stripeHooks/useSubscriptions';
import { useQueryClient } from 'react-query';
import { BookingProviderContext } from '@/contexts/BookingProfileContext';

const buttonClassName: Record<SubscriptionPlan, string> = {
  Free: 'freePlan',
  'Proficient Pioneer': 'proficientPioneerPlan',
  'Legendary Explorer': 'legendaryExplorerPlan',
  'Galactic Adventurer': 'galacticAdventurerPlan',
  'Nexus Elite': 'nexusElitePlan',
};

export const useCtaButton = ({
  plan,
  isCurrentPlan,
  id,
}: {
  plan: SubscriptionPlan;
  isCurrentPlan?: boolean;
  id: number;
}) => {
  const [modalType, setModalType] = useState<ModalType>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const { selectedBillingFrequency } = useContext(EditProfileContext);

  const { data: user } = useCurrentUser();
  const { data: subscriptions } = useSubscriptions();
  const { data: paymentMethods } = usePaymentMethods();
  const { mutateAsync: checkout } = useSubscriptionCheckout({
    onSuccess: async (res) => {
      if ('errors' in res) {
        console.error(res.errors);
        return;
      }

      if ('client_secret' in res) {
        localStorage.setItem(
          'client_secret_checkout_subscribe',
          res.client_secret,
        );

        window.location.href = '/account/stripe_checkout';
      }
    },
  });

  const userPlanId = user?.subscription
    ? SUBSCRIPTION_PLANS_CONFIG[user?.subscription?.plan_name]?.id
    : DEFAULT_PLAN_ID;
  const userPlanBillingFrequency =
    user?.subscription?.interval === 'year' ? 'yearly' : 'monthly';
  const isUserSubscriptionActive =
    user?.subscription && !user?.subscription?.cancel_at_period_end;
  const isUserSubscriptionActiveUntilPeriodEnd =
    user?.subscription && user?.subscription?.cancel_at_period_end;

  const getButtonTextAndType = () => {
    if (selectedBillingFrequency === userPlanBillingFrequency) {
      if (isCurrentPlan && isUserSubscriptionActive) {
        return { text: 'Cancel Subscription', type: 'cancel' };
      }

      return id >= userPlanId
        ? { text: 'Upgrade', type: 'upgrade' }
        : { text: 'Downgrade', type: 'downgrade' };
    }

    return {
      text: selectedBillingFrequency === 'yearly' ? 'Upgrade' : 'Downgrade',
      type: selectedBillingFrequency === 'yearly' ? 'upgrade' : 'downgrade',
    };
  };

  const { text, type } = getButtonTextAndType();

  const handleOpenModal = (type) => {
    setModalType(type);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalType(null);
    setIsModalOpen(false);
  };

  const getCurrentPlanDetails = () => {
    const foundCurrentPlanInfo = subscriptions?.find(
      (_plan) => _plan.name === plan,
    );

    const foundCurrentPlanPriceId = foundCurrentPlanInfo?.prices.find(
      (price) =>
        price.interval ===
        (selectedBillingFrequency === 'yearly' ? 'year' : 'month'),
    )?.id;

    return foundCurrentPlanInfo && foundCurrentPlanPriceId
      ? { foundCurrentPlanInfo, foundCurrentPlanPriceId }
      : null;
  };
  const currentPlanDetails = getCurrentPlanDetails();

  const queryClient = useQueryClient();
    const { showAlert } =
      useContext(EditProfileContext) || useContext(BookingProviderContext);

  const handleButtonClick = async () => {
    const priceId = currentPlanDetails?.foundCurrentPlanPriceId;
    if (!priceId) return;
    try{
      if (!user?.phone_verified_at || !user.phone) {
        document.cookie = `subscription_price_id=${priceId}; path=/; Secure`;
        window.location.href = '/users/phone_number_verification';
      }
  
      if (!user?.subscription) {
        return await checkout({ price_id: priceId });
      }
      else {console.log('error');
      }
  
      if (!paymentMethods || paymentMethods?.length === 0) {
        queryClient.invalidateQueries(['paymentMethods']);
        showAlert({
          type: 'error',
          title: 'Error',
          description: 'Payment method is invalid. Please go to the Billing tab, add a payment method, and try again.',
        });
      }
      if (
        (isUserSubscriptionActive ||
        isUserSubscriptionActiveUntilPeriodEnd)  &&
          paymentMethods &&
          paymentMethods?.length > 0
      ) {
        return handleOpenModal(type);
      }  
      
    } catch (error) {
      showAlert(
        {type: 'error',
          title: 'Error',
          description:error.message}
      )

    }
  };

  return {
    modalType,
    isModalOpen,
    buttonClassName,
    buttonText: text,
    currentPlanDetails,
    handleButtonClick,
    handleCloseModal,
    handleOpenModal,
  };
};
